import React, { useState } from 'react';
import { Incident, TypeIncident } from '../types';
import IncidentCard from './IncidentCard';
import {
  ExclamationTriangleIcon,
  PlusCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

interface IncidentsSectionProps {
  incidentsOuverts: Incident[];
  incidentsResolus: Incident[];
  onAjouterIncident?: (incident: Omit<Incident, 'id' | 'projetId'>) => void;
  onResoudreIncident?: (incidentId: string) => void;
}

const IncidentsSection: React.FC<IncidentsSectionProps> = ({
  incidentsOuverts,
  incidentsResolus,
  onAjouterIncident,
  onResoudreIncident,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [showResolus, setShowResolus] = useState(false);

  // Form state
  const [type, setType] = useState<TypeIncident>('technique');
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [impactFinancier, setImpactFinancier] = useState('');
  const [impactDelai, setImpactDelai] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!titre.trim() || !description.trim()) return;
    onAjouterIncident?.({
      type,
      titre: titre.trim(),
      description: description.trim(),
      dateDeclaration: new Date().toISOString().split('T')[0],
      statut: 'ouvert',
      impactFinancierEstime: impactFinancier ? parseFloat(impactFinancier) : undefined,
      impactDelaiEstime: impactDelai ? parseInt(impactDelai) : undefined,
    });
    setTitre('');
    setDescription('');
    setImpactFinancier('');
    setImpactDelai('');
    setShowForm(false);
  };

  return (
    <section className="p-6 bg-white shadow rounded-xl mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" />
          <h2 className="text-xl font-semibold text-gray-900">Incidents</h2>
          {incidentsOuverts.length > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-600">
              {incidentsOuverts.length} ouvert{incidentsOuverts.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {onAjouterIncident && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors"
          >
            <PlusCircleIcon className="w-4 h-4 mr-1" />
            Déclarer un incident
          </button>
        )}
      </div>

      {/* Formulaire inline */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-4 p-4 bg-violet-50 rounded-lg border border-violet-100 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as TypeIncident)}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="technique">Technique</option>
                <option value="securite">Sécurité</option>
                <option value="meteo">Météo</option>
                <option value="alea">Aléa</option>
                <option value="administratif">Administratif</option>
                <option value="environnemental">Environnemental</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Titre</label>
              <input
                type="text"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                placeholder="Ex: Fissure dalle R+2"
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              placeholder="Décrivez l'incident..."
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Impact financier estimé (€)</label>
              <input
                type="number"
                value={impactFinancier}
                onChange={(e) => setImpactFinancier(e.target.value)}
                placeholder="Ex: 25000"
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Impact délai estimé (semaines)</label>
              <input
                type="number"
                value={impactDelai}
                onChange={(e) => setImpactDelai(e.target.value)}
                placeholder="Ex: 2"
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="bg-violet-600 text-white hover:bg-violet-700 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
            >
              Déclarer
            </button>
          </div>
        </form>
      )}

      {/* Incidents ouverts */}
      {incidentsOuverts.length > 0 ? (
        <div className="space-y-2 mb-4">
          {incidentsOuverts.map(inc => (
            <IncidentCard
              key={inc.id}
              incident={inc}
              onResoudre={onResoudreIncident}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-sm text-gray-400 mb-4">
          Aucun incident ouvert
        </div>
      )}

      {/* Incidents résolus (repliable) */}
      {incidentsResolus.length > 0 && (
        <div>
          <button
            onClick={() => setShowResolus(!showResolus)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium transition-colors mb-2"
          >
            {showResolus ? (
              <ChevronDownIcon className="w-3 h-3" />
            ) : (
              <ChevronRightIcon className="w-3 h-3" />
            )}
            {incidentsResolus.length} incident{incidentsResolus.length > 1 ? 's' : ''} résolu{incidentsResolus.length > 1 ? 's' : ''}
          </button>
          {showResolus && (
            <div className="space-y-2">
              {incidentsResolus.map(inc => (
                <IncidentCard key={inc.id} incident={inc} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default IncidentsSection;
